import type { FC } from "react";
import { useEffect } from "react";

import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { selectCartPending } from "@jti/store/selectors/selectCartPending";

import { useLoyaltyProductPoints } from "@checkout/hooks";
import { useLoyaltyStatus } from "@checkout/hooks/useLoyaltyStatus";
import { usePrice } from "@checkout/hooks/usePrice";

import { PaymentSummary } from "../PaymentSummary/PaymentSummary";
import { SummaryLoyaltyPoints } from "../SummaryLoyaltyPoints";

type CartPaymentSummaryProps = {
  showLoyaltyPointsSummary?: boolean;
  showFreeShippingProgressBar?: boolean;
};

export const CartPaymentSummary: FC<CartPaymentSummaryProps> = ({
  showLoyaltyPointsSummary = false,
  showFreeShippingProgressBar = false,
}) => {
  const dispatch = useDispatch();
  const isLoyaltyActive = useLoyaltyStatus();

  const { loyaltyChoice } = useSelector((store) => store.loyalty);
  const items = useSelector((state) => state.cart.items);

  const { cartId, isFreeTrial, prices, pricesFetchStatus, fetchCurrentPrices } = usePrice(undefined, true);

  const defaultCartItemsPending = useSelector(selectCartPending);
  const freeTrialCartItemsLoading = useSelector((state) => state.freeTrialCart.areItemsLoading);
  const itemsPending = isFreeTrial ? freeTrialCartItemsLoading : defaultCartItemsPending;

  const cartPoints = useLoyaltyProductPoints(items);

  useEffect(() => {
    if (cartId && pricesFetchStatus === "idle" && !itemsPending) {
      // @ts-expect-error - TS doesn't know that fetchCurrentPrices. Depending on isFreeTrial, it will be either fetchPrices or fetchTrialsPrices used directly is not causing problems
      dispatch(fetchCurrentPrices(cartId));
    }
  }, [cartId, pricesFetchStatus, itemsPending]);

  if (pricesFetchStatus === "idle") {
    return null;
  }

  return (
    <PaymentSummary
      isPricesLoading={pricesFetchStatus === "pending"}
      prices={prices}
      showFreeShippingProgressBar={showFreeShippingProgressBar}
    >
      {showLoyaltyPointsSummary && isLoyaltyActive ? (
        <SummaryLoyaltyPoints
          hasLoyalty={isLoyaltyActive}
          totalLoyaltyPoints={cartPoints}
          loyaltyChoice={loyaltyChoice || 0}
        />
      ) : null}
    </PaymentSummary>
  );
};
